import {
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiCallOut,
  EuiModalFooter,
  EuiButtonEmpty,
  EuiButton,
  EuiSpacer,
} from "@inscopix/ideas-eui";
import { useDataTableContext } from "../store/DataTableProvider";
import { useState } from "react";
import { captureException } from "@sentry/react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { useProjectTablesTableContext } from "components/ProjectTablesTable/provider/ProjectTablesTableProvider";
import { shallow } from "zustand/shallow";
import { isDefined } from "utils/isDefined";
import { IdeasFileNotFound } from "@inscopix/ideas-hyperformula";
import { FileAssignmentsWarning } from "./FileAssignmentsWarning";

interface ModalDeleteTablesProps {
  onClose: () => void;
}

export const ModalDeleteTables = ({ onClose }: ModalDeleteTablesProps) => {
  const visibleSelectedTables = useProjectTablesTableContext(
    (s) => s.getVisibleSelectedTables(),
    shallow,
  );
  const deleteTable = useDataTableContext((s) => s.deleteTable);
  const { fileAssignments, files } = useDataTableContext((s) => {
    let assignments: ReturnType<typeof s.getFileAssignments> = {};

    visibleSelectedTables.forEach((table) => {
      if (table.kind === "analysis") {
        const resultColumns = table.columns.filter((c) => c.is_result);
        const _assignments = s.getFileAssignments({
          addressRanges: resultColumns.map((c) => ({ columnId: c.id })),
        });
        assignments = { ...assignments, ..._assignments };
      } else if (table.kind === "data") {
        const _assignments = s.getFileAssignments({
          addressRanges: [{ tableId: table.id }],
          maxRefCount: 1,
        });
        assignments = { ...assignments, ..._assignments };
      }
    });

    const files = Object.keys(assignments)
      .map((fileId) => {
        const file = s.fileMap.filesById[fileId];
        return file !== IdeasFileNotFound ? file : undefined;
      })
      .filter(isDefined);

    return { fileAssignments: assignments, files };
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    // track successes
    let successCount = 0;

    // try to delete all selected table
    for (const table of visibleSelectedTables) {
      const fileIds = Object.entries(fileAssignments)
        .filter(([_, addresses]) =>
          addresses.some((a) => a.tableId === table.id),
        )
        .map(([fileId]) => fileId);

      try {
        await deleteTable({ tableId: table.id, fileIds });
        successCount++;
      } catch (err) {
        captureException(err);
      }
    }
    setIsLoading(false);

    // all tables deleted successfully
    if (successCount === visibleSelectedTables.length) {
      addUtilityToastSuccess("All tables deleted successfully");
    }
    // not all but some tables deleted - show failure toast
    else if (successCount > 0) {
      addUtilityToastFailure(
        `${successCount} table(s) deleted successfully, but some failed`,
      );
    }
    // no tables deleted - show failure toast
    else {
      addUtilityToastFailure("Failed to delete tables");
    }

    // modal closed - tables will remain selected if not deleted (managed upstream)
    onClose();
  };

  const deletionMessage = (() => {
    if (files.length > 0) {
      if (
        visibleSelectedTables.some((t) => t.kind === "data") &&
        visibleSelectedTables.some((t) => t.kind === "analysis")
      ) {
        return (
          <p>
            You are deleting data and analysis tables. Deleting data tables will
            automatically delete any uploaded files assigned to cells in these
            tables unless the file is also assigned in at least one other data
            table (assignments to analysis table inputs are not considered).
            Deleting analysis tables will automatically delete any output files
            from tasks run in these tables. Input files will not be deleted.
          </p>
        );
      } else if (visibleSelectedTables.some((t) => t.kind === "data")) {
        return (
          <p>
            Deleting data tables will automatically delete any uploaded files
            assigned to cells in these tables unless the file is also assigned
            in at least one other data table (assignments to analysis table
            inputs are not considered).
          </p>
        );
      } else {
        return (
          <p>
            Deleting analysis tables will automatically delete any output files
            from tasks run in these tables. Input files will not be deleted.
          </p>
        );
      }
    } else {
      return (
        <p>
          No files will be deleted. Deleting data tables only deletes uploaded
          files that are assigned to cells in the table and not assigned in at
          least one other data table. Deleting analysis tables only deletes
          output files, input files are not deleted.
        </p>
      );
    }
  })();

  return (
    <EuiModal onClose={onClose}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>Confirm Deletion</EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiCallOut
          title="Proceed with caution!"
          color="warning"
          iconType="warning"
        >
          {deletionMessage}
          <p>
            You are deleting:
            {visibleSelectedTables.map((table) => (
              <li key={table.id}>{table.name}</li>
            ))}
          </p>

          {files.length > 0 && (
            <>
              <FileAssignmentsWarning files={files} />
              <EuiSpacer size="m" />
            </>
          )}

          <p>
            This action cannot be undone. Any references to cells in deleted
            tables in other data and analysis tables will become invalid.
          </p>
        </EuiCallOut>
      </EuiModalBody>
      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton
          isLoading={isLoading}
          fill
          color="danger"
          onClick={() => void handleSubmit()}
        >
          Delete
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
